import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getVisaNotesUrl } from '../utils/visaApplicationNotesApi';

const NoteCard = ({ note }) => (
  <div className="border border-gray-100 rounded-xl p-3 bg-gray-50/60">
    {note.title && <p className="text-sm font-medium text-gray-800">{note.title}</p>}
    {note.body && (
      <p className={`text-sm text-gray-600 whitespace-pre-wrap ${note.title ? 'mt-1' : ''}`}>
        {note.body}
      </p>
    )}
  </div>
);

const NoteGroup = ({ label, notes }) => {
  if (!notes?.length) return null;

  return (
    <div>
      <p className="font-medium text-gray-700 mb-2">{label}</p>
      <div className="space-y-2">
        {notes.map((note, index) => (
          <NoteCard key={note.id || index} note={note} />
        ))}
      </div>
    </div>
  );
};

/**
 * Visa notes and extra notes for an application's route, loaded from
 * the Vughy route-package API through our own API.
 */
const VisaNotesSection = ({ application, role }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const notesUrl = application?.id ? getVisaNotesUrl(application.id, role) : null;

  useEffect(() => {
    if (!notesUrl) return undefined;

    let cancelled = false;

    const fetchNotes = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(notesUrl);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load visa notes');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchNotes();

    return () => {
      cancelled = true;
    };
  }, [notesUrl]);

  if (!notesUrl) return null;

  const hasNotes = Boolean(
    data?.notes?.length || data?.extra_notes?.length || data?.process
  );

  return (
    <div>
      <p className="font-medium text-gray-700 mb-2">Visa Notes</p>

      {loading ? (
        <p className="text-sm text-gray-400">Loading visa notes...</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !hasNotes ? (
        <p className="text-sm text-gray-400">No notes for this route and visa type.</p>
      ) : (
        <div className="space-y-4">
          {data.process && <NoteGroup label="Process" notes={[data.process]} />}
          <NoteGroup label="Notes" notes={data.notes} />
          <NoteGroup label="Extra Notes" notes={data.extra_notes} />
        </div>
      )}
    </div>
  );
};

export default VisaNotesSection;
